import GateKeeperMiddleware from '@/middleware/GateKeeperMiddleware.js';
import { redirectToLegacy, redirectToRedesign } from '@/router/courses/gradebook/gradebookRedesignGuards.js';

/**
 * The STANDALONE activity grader — the one Submissions and the four remediation surfaces
 * push, outside any gradebook.
 *
 * It renders the SAME page component as the gradebook grader but keeps its OWN route
 * record, because the two are authorised differently: this one requires `view grade`, the
 * gradebook one `view gradebook`. Redirecting into the gradebook record would silently
 * change who can open a grader.
 *
 * ⚠ `meta` sits on the CHILD here and on the PARENT for the gradebook route — the mirror
 * image. `callRouteMetaMiddleware` filters `to.matched` to records declaring
 * `meta.middleware`, so both work, but the shapes are not interchangeable and copying one
 * onto the other ships it ungated.
 */
export default [
    {
        path: '/courses/:courseId/activity/:activityId/user/:userId/activity-grader/:submissionId',
        component: () => import('@/pages/courses/activity-grader/ActivityGrader.vue'),
        props: true,

        children: [
            {
                name: 'activity-grader-view',
                path: '',
                component: () => import('@/components/activity-grader/ActivityGraderView.vue'),
                beforeEnter: [redirectToRedesign],
                meta: {
                    title: 'Quiz Grades',
                    middleware: [GateKeeperMiddleware],
                    ability: { action: 'view', subject: 'grade' },
                },
            },
        ],
    },
    {
        /**
         * The redesigned standalone grader. `grader/` is static so it cannot be confused
         * with the legacy path, and the params match it exactly so the two guards can
         * forward `to.params` verbatim in both directions.
         */
        name: 'activity-grader-redesign',
        path: '/courses/:courseId/activity/:activityId/user/:userId/grader/:submissionId',
        component: () => import('@/pages/courses/gradebook/GradebookGraderPage.vue'),
        beforeEnter: [redirectToLegacy],
        meta: {
            title: 'Quiz Grades',
            middleware: [GateKeeperMiddleware],
            // `grade`, NOT `gradebook`. This is the whole reason it is a separate record.
            ability: { action: 'view', subject: 'grade' },
        },
        props: route => ({ ...route.params, entryPoint: 'standalone' }),
    },
];
